import {Component, computed, inject, signal, ChangeDetectionStrategy} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {Settings} from "@app/common/models/settings/settings"
import {DialogService} from "@app/common/services/dialog/dialog.service"
import {OrganizationsService} from "@app/common/services/organizations/organizations.service"
import {MaterialReference} from "@cm/template-nodes"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {GetMaterialDetailsForMaterialReferenceInspectorGQL} from "@template-editor/components/inspectors/material-reference-inspector/material-reference-inspector.generated"
import {catchError, from, of, switchMap} from "rxjs"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {CardComponent} from "@app/common/components/cards/card/card.component"
import {DataObjectThumbnailComponent} from "@app/common/components/data-object-thumbnail/data-object-thumbnail.component"
import {ThumbnailComponent} from "@app/common/components/thumbnail/thumbnail.component"
import {SelectionPossibilities, ValueSlotComponent} from "@app/template-editor/components/value-slot/value-slot.component"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"
import {z} from "zod/v4-mini"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-reference-inspector",
    templateUrl: "./material-reference-inspector.component.html",
    styleUrl: "./material-reference-inspector.component.scss",
    imports: [
        InspectorSectionComponent,
        ValueSlotComponent,
        CardComponent,
        ButtonComponent,
        DataObjectThumbnailComponent,
        ThumbnailComponent,
        RouterModule,
        MatTooltipModule,
    ],
})
export class MaterialReferenceInspectorComponent extends BaseInspectorComponent<MaterialReference> {
    private readonly materialGql = inject(GetMaterialDetailsForMaterialReferenceInspectorGQL)
    readonly dialogService = inject(DialogService)
    readonly organizations = inject(OrganizationsService)
    readonly $materialId = computed(() => {
        return this.$parameters().materialId
    })
    readonly $materialRevisionId = computed(() => this.$parameters().materialRevisionId)
    private readonly $materialData = toSignal(
        toObservable(this.$materialId).pipe(
            switchMap((materialId) => {
                return from(fetchThrowingErrors(this.materialGql)({legacyId: materialId}))
            }),
            catchError(() => of(null)),
        ),
        {
            initialValue: null,
        },
    )
    readonly $thumbnailUrl = computed(() => {
        const materialRevisionData = this.$materialData()
        if (!materialRevisionData) return Settings.IMAGE_NOT_AVAILABLE_SMALL_URL
        return undefined
    })
    readonly $thumbnailObject = computed(() => {
        const materialRevisionData = this.$materialData()
        if (materialRevisionData) return materialRevisionData.material.galleryImage
        return undefined
    })
    readonly $title = computed(() => {
        const materialRevisionData = this.$materialData()
        if (!materialRevisionData) return this.$parameters().name

        const {material} = materialRevisionData

        return `${material.legacyId} - ${material.name}`
    })
    readonly $boolean = signal(z.boolean())
    readonly $number = signal(z.number())
    readonly $autoUpdate = computed(() => {
        return this.$parameters().materialRevisionId === null
    })
    readonly $latestCyclesRevisionId = computed(() => {
        const materialRevisionData = this.$materialData()
        return materialRevisionData?.material.latestCyclesRevision?.legacyId
    })

    readonly $revisionPossibilities = computed<SelectionPossibilities<number>>(() => {
        const materialRevisionData = this.$materialData()
        return (
            materialRevisionData?.material.cyclesRevisions?.map((revision) => ({
                value: revision.legacyId,
                name: `v${revision.number}`,
            })) ?? []
        )
    })

    onChangeAutoUpdate(autoUpdate: unknown) {
        if (typeof autoUpdate !== "boolean") return
        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({materialRevisionId: autoUpdate ? null : this.$latestCyclesRevisionId()})
        })
    }

    updateToLatestCyclesRevision() {
        const latestCyclesRevisionId = this.$latestCyclesRevisionId()
        if (latestCyclesRevisionId === undefined) return
        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({materialRevisionId: latestCyclesRevisionId})
        })
    }
}
